
import { makeStyles } from '@material-ui/core/styles';

/**
 * Class list grid columns — shared by tableHeader/classRow so header cells
 * line up with row cells.
 *
 * showSchool (SNW-ALL-My Classes-Main, a network viewer) inserts a School
 * column right after Class — CLASS | SCHOOL | GRADE | STUDENTS | LICENSES |
 * PROGRAMS | ACTIONS, per the approved mockup. The non-network case is left
 * as the exact original strings (not re-derived from a shared formula), so a
 * single school's own class list — teacher or not — renders byte-identical
 * to before this was added.
 */
/*
 * showLcTeachers (school / network, My Classes deck 30 Sep 2026 A1) adds an
 * "LC Teachers" count column just before Actions.
 */
const classGridColumns = (props, bp) => {
    const cols = baseClassGridColumns(props, bp);
    if (!props.showLcTeachers) return cols;
    const at = cols.lastIndexOf(' minmax(');
    return at === -1 ? `${cols} 0.8fr` : `${cols.slice(0, at)} 0.8fr${cols.slice(at)}`;
};

const baseClassGridColumns = (props, bp) => {
    /*
     * A single school's own list without the Licenses column (Ian-SW-29Sep-6):
     * the original widths minus that column, so its four action buttons keep
     * the wide Actions column instead of the teacher's five equal columns.
     */
    if (!props.showSchool && props.hideLicenses && props.wideActions) {
        return {
            base: '1.2fr 1fr 0.9fr 0.9fr minmax(380px, 2.8fr)',
            md: '1.2fr 1fr 0.9fr 0.9fr minmax(220px, 2fr)',
            sm: '1fr 0.7fr 0.6fr 0.7fr minmax(190px, 1.5fr)',
        }[bp];
    }
    if (!props.showSchool) {
        const original = {
            base: props.hideLicenses ? 'repeat(5, 1fr)' : '1.2fr 1fr 0.9fr 1.3fr 0.9fr minmax(380px, 2.8fr)',
            md: props.hideLicenses ? 'repeat(5, 1fr)' : '1.2fr 1fr 0.9fr 1.3fr 0.9fr minmax(220px, 2fr)',
            sm: props.hideLicenses ? 'repeat(5, 1fr)' : '1fr 0.7fr 0.6fr 1fr 0.7fr minmax(190px, 1.5fr)',
        };
        return original[bp];
    }
    const withSchool = {
        base: props.hideLicenses
            ? '1.2fr 1.1fr 1fr 0.9fr 0.9fr minmax(380px, 2.8fr)'
            : '1.2fr 1.1fr 1fr 0.9fr 1.3fr 0.9fr minmax(380px, 2.8fr)',
        md: props.hideLicenses
            ? '1.2fr 1fr 1fr 0.9fr 0.9fr minmax(220px, 2fr)'
            : '1.2fr 1fr 1fr 0.9fr 1.3fr 0.9fr minmax(220px, 2fr)',
        sm: props.hideLicenses
            ? '1fr 0.8fr 0.7fr 0.6fr 0.7fr minmax(190px, 1.5fr)'
            : '1fr 0.8fr 0.7fr 0.6fr 1fr 0.7fr minmax(190px, 1.5fr)',
    };
    return withSchool[bp];
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#F5F0EB',
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        '& .MuiTypography-root': {
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif !important",
        },
        '& .MuiButton-root': {
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif !important",
        }
    },
    sidebar: {
        width: '215px',
        background: '#ffffff',
        color: '#555',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        height: '100vh',
        left: 0,
        top: 0,
        zIndex: 100,
        borderRight: '1px solid #e8e8e8',
        boxShadow: '2px 0 10px rgba(0,0,0,0.04)',
        [theme.breakpoints.down('md')]: {
            display: 'none',
        },
    },
    sidebarLogoContainer: {
        padding: '18px 20px 16px',
        flexShrink: 0,
        borderBottom: '1px solid #ebebeb',
    },
    logo: {
        height: '36px',
    },
    navItems: {
        flexGrow: 1,
        marginTop: '8px',
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '4px' },
    },
    navItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 20px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        margin: '2px 10px',
        borderRadius: '10px',
        color: '#888',
        '& svg': {
            fontSize: '20px !important',
            color: '#aaa',
            flexShrink: 0,
        },
        '&:hover': {
            backgroundColor: '#f7f7f7',
        },
    },
    navItemActive: {
        backgroundColor: '#F15A24 !important',
        color: '#fff !important',
        boxShadow: '0 4px 12px rgba(241, 90, 36, 0.25)',
        '& svg': {
            color: '#fff !important',
        },
        '& $navText': {
            color: '#fff !important',
        },
        '&:hover': {
            backgroundColor: '#e04d18 !important',
        },
    },
    navItemParentActive: {
        '& $navText': {
            color: '#F15A24 !important',
            fontWeight: '700 !important',
        },
        '& svg': {
            color: '#F15A24 !important',
        },
    },
    navText: {
        marginLeft: '12px !important',
        fontSize: '14px !important',
        fontWeight: '500 !important',
        color: '#666',
        flexGrow: 1,
    },
    expandIcon: {
        fontSize: '18px !important',
        color: 'inherit',
        opacity: 0.7,
    },
    navDivider: {
        height: '1px',
        backgroundColor: '#ebebeb',
        margin: '2px 10px',
    },
    subNav: {
        paddingLeft: '52px',
        paddingRight: '10px',
        paddingTop: '8px',
        paddingBottom: '8px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#999',
        cursor: 'pointer',
        margin: '1px 10px',
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
            color: '#F15A24',
            backgroundColor: '#fdf5f2',
        },
    },
    subNavActive: {
        backgroundColor: '#F15A24 !important',
        color: '#fff !important',
        fontWeight: '600 !important',
        borderRadius: '8px',
        '&:hover': {
            backgroundColor: '#e04d18 !important',
            color: '#fff !important',
        },
    },
    mainContent: {
        marginLeft: '215px',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('md')]: {
            marginLeft: 0,
        },
    },
    mainContentInterior: {
        padding: '20px 60px 40px',
        [theme.breakpoints.down('md')]: {
            padding: '20px 30px',
        },
        [theme.breakpoints.down('sm')]: {
            padding: '15px 15px',
        },
    },
    header: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '30px',
        position: 'relative',
        paddingTop: '30px',
    },
    headerTitle: {
        fontSize: '28px !important',
        fontWeight: '800 !important',
        color: '#111',
        [theme.breakpoints.down('sm')]: {
            fontSize: '20px !important',
            padding: '0 15px',
            textAlign: 'center',
            lineHeight: '1.2 !important',
        },
    },
    profileIconBtn: {
        position: 'absolute !important',
        right: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
    },
    profileIcon: {
        fontSize: '38px !important',
        color: '#999',
        cursor: 'pointer',
    },
    // Legacy - kept for compatibility
    addClassWrapper: {
        display: 'none',
    },
    headerActionsBox: {
        display: 'flex',
        gap: '10px',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    downloadCsvBtn: {
        background: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '10px 22px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '14px !important',
        whiteSpace: 'nowrap',
        boxShadow: '0 3px 10px rgba(241, 90, 36, 0.25)',
        '&:hover': {
            background: '#e04d18 !important',
        },
        // The `!important` background above beats MUI's own disabled styling, so
        // a disabled button still looked live (orange, hover, shadow). Restate it
        // here — grey, flat, no hover — so "disabled" is visually obvious.
        '&.Mui-disabled': {
            background: 'var(--brand-border, #E5E7EB) !important',
            color: 'var(--brand-text-muted, #9CA3AF) !important',
            boxShadow: 'none !important',
            cursor: 'not-allowed !important',
        },
        '&.Mui-disabled:hover': {
            background: 'var(--brand-border, #E5E7EB) !important',
        },
    },
    addClassBtn: {
        background: '#B7D334 !important',
        color: '#000000 !important',
        borderRadius: '50px !important',
        padding: '10px 20px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '14px !important',
        display: 'flex !important',
        alignItems: 'center !important',
        whiteSpace: 'nowrap',
        boxShadow: '0 3px 10px rgba(124, 181, 24, 0.25)',
        '&:hover': {
            background: '#6aa012 !important',
        },
    },
    tableContent: {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '20px',
    },
    tableHeader: (props) => ({
        display: 'grid',
        // Licenses now holds "N used / N available" (was a bare total number),
        // and Programs is its own new column — swapped from 0.9fr/1.3fr so each
        // gets width matching what it actually holds, with room between them.
        // Teacher workspace hides the Licenses column entirely (licenses are a
        // school-level concern, not a teacher's), collapsing to 5 columns.
        // showSchool (a network viewer) adds a School column — see
        // classGridColumns above.
        gridTemplateColumns: classGridColumns(props, 'base'),
        columnGap: '16px',
        padding: '10px 20px',
        marginBottom: '25px',
        minWidth: props.showSchool ? '1220px' : '1100px',
        alignItems: 'center',
        borderRadius: '10px',
        backgroundColor: 'var(--brand-bg, #FFFBF7)',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: classGridColumns(props, 'md'),
            minWidth: props.showSchool ? '880px' : '780px',
            padding: '10px !important',
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: classGridColumns(props, 'sm'),
            minWidth: props.showSchool ? '700px' : '620px',
            padding: '8px !important',
            marginBottom: '12px',
        },
    }),
    headerCell: {
        fontSize: '15px !important',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
        textAlign: 'left',
        [theme.breakpoints.down('md')]: {
            fontSize: '13px !important',
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '11px !important',
        },
    },
    classRow: (props) => ({
        display: 'grid',
        // Matches tableHeader's column proportions/gap so cells line up under
        // their labels — see that rule for why 4/5 were swapped, why teacher
        // workspace collapses this to 5 columns (no Licenses), and why a
        // network viewer (showSchool) gets a 6th/7th School column.
        gridTemplateColumns: classGridColumns(props, 'base'),
        columnGap: '16px',
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        padding: '14px 20px',
        alignItems: 'center',
        marginBottom: '12px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        minWidth: props.showSchool ? '1220px' : '1100px',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: classGridColumns(props, 'md'),
            minWidth: props.showSchool ? '880px' : '780px',
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: classGridColumns(props, 'sm'),
            minWidth: props.showSchool ? '700px' : '620px',
            padding: '10px 12px',
        },
    }),
    cellText: {
        fontSize: '15px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'left',
        [theme.breakpoints.down('md')]: {
            fontSize: '13px !important',
        },
        [theme.breakpoints.down('sm')]: {
            fontSize: '11px !important',
        },
    },
    draftRow: {
        display: 'grid',
        gridTemplateColumns: '1.2fr 1fr 0.9fr 0.9fr 1.3fr minmax(380px, 2.8fr)',
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        padding: '14px 20px',
        alignItems: 'center',
        marginBottom: '12px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        minWidth: '1100px',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1.2fr 1fr 0.9fr 0.9fr 1.3fr minmax(220px, 2fr)',
            minWidth: '780px',
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr 0.7fr 0.6fr 0.6fr 0.9fr minmax(190px, 1.5fr)',
            minWidth: '620px',
            padding: '10px 12px',
        },
    },
    draftBadge: {
        backgroundColor: '#F15A24',
        color: '#fff',
        padding: '4px 12px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: '900',
        textTransform: 'uppercase',
        display: 'inline-block',
        letterSpacing: '0.5px',
    },
    resumeBtn: {
        background: 'linear-gradient(90deg, #F15A24 0%, #FF8A00 100%) !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 22px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13px !important',
        [theme.breakpoints.down('sm')]: {
            minWidth: '90px !important',
            fontSize: '12px !important',
            padding: '5px 12px !important',
        }
    },
    // Always a 2-per-row grid — two buttons per line on every screen size,
    // rather than a single row that has to shrink/scroll to fit all four.
    actionGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '8px',
        width: '100%',
        '& > span, & > .MuiButton-root': {
            flex: '0 0 calc(42% - 4px)',
        },
        '& > span': {
            display: 'inline-flex',
            alignItems: 'center',
        },
        '& .MuiButton-root': {
            whiteSpace: 'nowrap',
            width: '100%',
        },
    },
    viewBtn: {
        background: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 18px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13px !important',
        minWidth: '70px !important',
        [theme.breakpoints.down('md')]: {
            fontSize: '12px !important',
            padding: '5px 12px !important',
            minWidth: '60px !important',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '60px !important',
            fontSize: '12px !important',
            padding: '5px 10px !important',
        }
    },
    linkBtn: {
        background: '#6A8EC9 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 16px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13px !important',
        minWidth: '88px !important',
        border: 'none !important',
        [theme.breakpoints.down('md')]: {
            fontSize: '12px !important',
            padding: '5px 10px !important',
            minWidth: '80px !important',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '80px !important',
            fontSize: '12px !important',
            padding: '5px 10px !important',
        },
        // Disabled: same blue, reduced opacity — clear "not available" signal
        // that stays on-brand without introducing a foreign color
        '&.Mui-disabled': {
            background: '#6A8EC9 !important',
            color: '#fff !important',
            opacity: '0.38 !important',
        },
    },
    // Link Selection Dialog Styles
    linkSelectionPaper: {
        borderRadius: '50px !important',
        padding: '60px 80px !important',
        maxWidth: '850px !important',
        width: '100% !important',
        textAlign: 'center',
        boxShadow: '0 10px 50px rgba(0,0,0,0.15) !important',
        backgroundColor: 'var(--brand-surface, #fff) !important',
        backgroundImage: 'none !important',
    },
    linkSelectionTitle: {
        fontSize: '28px !important',
        fontWeight: '800 !important',
        color: 'var(--brand-text, #000)',
        marginBottom: '50px !important',
        textAlign: 'center',
        lineHeight: '1.3 !important',
    },
    selectionCard: {
        flex: 1,
        backgroundColor: 'var(--brand-bg, #f9fcfd)',
        borderRadius: '32px',
        padding: '40px 30px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        cursor: 'pointer',
        border: '1px solid var(--brand-border, #e0ebed)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            borderColor: '#F15A24',
            backgroundColor: '#F15A24',
            boxShadow: '0 20px 40px rgba(241, 90, 36, 0.25)',
            transform: 'translateY(-12px)',
            '& $selectionIcon': {
                color: '#fff !important',
            },
            '& $selectionTitle': {
                color: '#fff !important',
            },
            '& $selectionDesc': {
                color: 'rgba(255,255,255,0.9) !important',
            }
        }
    },
    selectionIcon: {
        fontSize: '64px !important',
        color: '#F15A24',
        marginBottom: '3px !important',
        transition: 'all 0.3s ease',
    },
    selectionTitle: {
        fontSize: '24px !important',
        fontWeight: '800 !important',
        color: 'var(--brand-text, #111)',
        transition: 'color 0.3s ease',
    },
    selectionDesc: {
        fontSize: '16px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text-muted, #666)',
        lineHeight: '1.6 !important',
        transition: 'color 0.3s ease',
    },
    addLicensesBtn: {
        background: '#C91C6E !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 16px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13px !important',
        minWidth: '88px !important',
        [theme.breakpoints.down('md')]: {
            fontSize: '12px !important',
            padding: '5px 10px !important',
            minWidth: '80px !important',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '80px !important',
            fontSize: '12px !important',
            padding: '5px 10px !important',
        }
    },
    dashboardBtn: {
        background: '#052F44 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 12px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '12px !important',
        minWidth: '80px !important',
        [theme.breakpoints.down('md')]: {
            fontSize: '11px !important',
            padding: '5px 8px !important',
            minWidth: '70px !important',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '70px !important',
            fontSize: '11px !important',
            padding: '5px 8px !important',
        }
    },
    addStudentsBtn: {
        background: '#2E9E6A !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '7px 14px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13px !important',
        minWidth: '96px !important',
        [theme.breakpoints.down('md')]: {
            fontSize: '12px !important',
            padding: '5px 10px !important',
            minWidth: '80px !important',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '80px !important',
            fontSize: '12px !important',
            padding: '5px 10px !important',
        }
    },
    // Add Class Dialog Styles
    dialogPaper: {
        borderRadius: '50px !important',
        padding: '50px 60px !important',
        maxWidth: '700px !important',
        width: '100% !important',
        textAlign: 'center',
        boxShadow: '0 10px 50px rgba(0,0,0,0.15) !important',
    },
    dialogTitle: {
        fontSize: '28px !important',
        fontWeight: '800 !important',
        color: 'var(--brand-text, #000)',
    },
    formRow: {
        marginBottom: '22px',
        width: '100%',
    },
    inputField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: 'var(--brand-surface, #fff)',
            '& fieldset': {
                borderColor: '#ccc',
            },
            '&:hover fieldset': {
                borderColor: '#0097b2',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#0097b2',
            },
        },
        '& .MuiInputBase-input': {
            padding: '12px 14px',
            fontSize: '16px',
            fontWeight: '500',
            color: 'var(--brand-text, #333)',
        },
        '& .MuiInputLabel-root': {
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--brand-text-muted, #333)',
            transform: 'translate(14px, 12px) scale(1)',
            '&.Mui-focused, &.MuiFormLabel-filled': {
                transform: 'translate(14px, -10px) scale(0.9)',
                color: '#000',
                background: 'transparent',
                fontWeight: '500',
                padding: '0 5px',
            }
        },
    },
    selectField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
        },
    },
    createActionBtn: {
        background: 'linear-gradient(90deg, #F15A24 0%, #FF8A00 100%) !important',
        color: '#fff !important',
        borderRadius: '15px !important',
        padding: '12px 60px !important',
        fontSize: '20px !important',
        fontWeight: '800 !important',
        textTransform: 'none !important',
        marginTop: '30px !important',
        boxShadow: '0 4px 15px rgba(241, 90, 36, 0.3)',
    },
    // Download Dialog Styles
    downloadDialogTitle: {
        fontSize: '24px !important',
        fontWeight: '800 !important',
        color: 'var(--brand-text, #000)',
        marginBottom: '40px !important',
        textAlign: 'center',
    },
    downloadBtn: {
        background: 'linear-gradient(90deg, #F15A24 0%, #FF8A00 100%) !important',
        color: '#fff !important',
        borderRadius: '12px !important',
        padding: '12px 40px !important',
        fontSize: '18px !important',
        fontWeight: '800 !important',
        textTransform: 'none !important',
        boxShadow: '0 4px 15px rgba(241, 90, 36, 0.3)',
    },
    // Student Table Styles
    studentsDialogPaper: {
        borderRadius: '40px !important',
        padding: '32px 40px !important',
        maxWidth: '1100px !important',
        width: '100% !important',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2) !important',
        maxHeight: '92vh !important',
        overflow: 'hidden !important',
        display: 'flex !important',
        flexDirection: 'column !important',
        backgroundColor: 'var(--brand-bg, #FFFBF7) !important',
    },
    // Edit Class dialog — same paper mechanics as studentsDialogPaper (rounded,
    // flex column, internal scroll, 92vh cap) but noticeably narrower: this
    // dialog holds a short form (class details + LC teachers), not the wide
    // multi-tab class/roster builder.
    editClassDialogPaper: {
        borderRadius: '40px !important',
        padding: '32px 40px !important',
        maxWidth: '680px !important',
        width: '100% !important',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2) !important',
        maxHeight: '92vh !important',
        overflow: 'hidden !important',
        display: 'flex !important',
        flexDirection: 'column !important',
        backgroundColor: 'var(--brand-bg, #FFFBF7) !important',
    },
    studentsDialogScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: '4px',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '10px',
        },
    },
    studentsDialogFooter: {
        flexShrink: 0,
        paddingTop: '12px',
        marginTop: '4px',
        borderTop: '1px solid var(--brand-border, #eee)',
        backgroundColor: 'var(--brand-bg, #FFFBF7)',
    },
    enrollModulesPanel: {
        backgroundColor: 'var(--brand-bg, #FAFAFA)',
        border: '1px solid var(--brand-border, #F0F0F0)',
        borderRadius: '12px',
        padding: '12px 16px',
        marginBottom: '16px',
        maxHeight: '112px',
        overflowY: 'auto',
        flexShrink: 0,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '4px' },
    },
    studentsTableContainer: {
        width: '100%',
        maxHeight: 'min(320px, 40vh)',
        overflowY: 'auto',
        overflowX: 'auto',
        marginTop: '16px',
        marginBottom: '8px',
        border: '1px solid var(--brand-border, #e0e0e0)',
        borderRadius: '16px',
        backgroundColor: 'var(--brand-surface, #fff)',
        '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#ccc',
            borderRadius: '10px',
        },
    },
    studentsTable: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    // The manual student-entry table (First/Last Name, Year of Birth, Learner/
    // Parent Email, Gender, Country, State, Postal Code, ...) has too many
    // columns to fit width:100% without every column being squeezed unreadably
    // narrow. No width constraint here — each <th> below carries its own
    // minWidth, so the table grows to its natural content width and the
    // container's own overflowX:auto scrolls it, instead of cramming columns.
    studentsTableWide: {
        borderCollapse: 'collapse',
    },
    studentsTh: {
        textAlign: 'left',
        padding: '15px 20px',
        backgroundColor: 'var(--brand-bg, #f8f9fa)',
        borderBottom: '2px solid var(--brand-border, #eee)',
        fontSize: '14px',
        fontWeight: '800',
        color: 'var(--brand-text, #444)',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    studentsThIndex: {
        width: '50px',
        textAlign: 'center',
        padding: '15px 10px',
        backgroundColor: 'var(--brand-bg, #f8f9fa)',
        borderBottom: '2px solid var(--brand-border, #eee)',
        borderRight: '1px solid var(--brand-border, #eee)',
        fontSize: '14px',
        fontWeight: '800',
        color: 'var(--brand-text-muted, #888)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    studentsThAction: {
        width: '50px',
        padding: '15px 10px',
        backgroundColor: 'var(--brand-bg, #f8f9fa)',
        borderBottom: '2px solid var(--brand-border, #eee)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    studentsTdIndex: {
        textAlign: 'center',
        borderBottom: '1px solid var(--brand-border, #eee)',
        borderRight: '1px solid var(--brand-border, #eee)',
        backgroundColor: 'var(--brand-bg, #fafafa)',
        fontSize: '14px',
        fontWeight: '600',
        color: 'var(--brand-text-muted, #888)',
    },
    studentsTdAction: {
        textAlign: 'center',
        borderBottom: '1px solid var(--brand-border, #eee)',
    },
    studentsTd: {
        padding: '0',
        borderBottom: '1px solid var(--brand-border, #eee)',
    },
    // Read-only text cell (Existing Students tab). Unlike studentsTd — which is
    // padding:0 because it wraps a self-padded <input> — this holds plain text,
    // so it needs its own padding to line up under the studentsTh headers.
    studentsTdText: {
        padding: '12px 20px',
        borderBottom: '1px solid var(--brand-border, #eee)',
        fontSize: '15px',
        fontWeight: '500',
        color: 'var(--brand-text, #333)',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    studentsInput: {
        width: '100%',
        padding: '12px 16px',
        border: 'none',
        borderRight: '1px solid var(--brand-border, #f0f0f0)',
        outline: 'none',
        fontSize: '15px',
        fontWeight: '500',
        color: 'var(--brand-text, #333)',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        transition: 'all 0.2s',
        '&:focus': {
            backgroundColor: 'rgba(0, 151, 178, 0.04)',
            boxShadow: 'inset 0 0 0 2px #0097b2',
            borderRightColor: 'transparent',
        },
        '&::placeholder': {
            color: 'var(--brand-text-muted, #bbb)',
            fontWeight: '400',
        }
    },
    studentsDeleteBtn: {
        color: '#ff4d4f !important',
        padding: '8px !important',
        '&:hover': {
            backgroundColor: 'rgba(255, 77, 79, 0.1) !important',
        }
    },
    studentsActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    addRowBtn: {
        color: '#FF6900 !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13.5px !important',
        padding: '4px 10px !important',
        minWidth: 'auto !important',
        '&:hover': {
            backgroundColor: 'rgba(255, 105, 0, 0.06) !important',
        }
    },
    saveAllBtn: {
        background: '#FF6900 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '10px 28px !important',
        fontSize: '14.5px !important',
        fontWeight: '700 !important',
        textTransform: 'none !important',
        boxShadow: '0 4px 12px rgba(255, 105, 0, 0.25)',
        '&:hover': {
            background: '#e55e00 !important',
        },
        '&.Mui-disabled': {
            background: 'var(--brand-border, #E5E7EB) !important',
            color: 'var(--brand-text-muted, #9CA3AF) !important',
            boxShadow: 'none !important',
        },
    },
    studentTabsContainer: {
        display: 'flex',
        borderBottom: '1px solid var(--brand-border, #e0e0e0)',
        marginBottom: '20px',
        gap: '10px',
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: '4px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '4px' },
    },
    studentTab: {
        padding: '10px 20px',
        cursor: 'pointer',
        fontWeight: '800',
        fontSize: '16px',
        color: 'var(--brand-text-muted, #888)',
        borderBottom: '3px solid transparent',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
        '&:hover': {
            color: '#F15A24',
        }
    },
    studentTabActive: {
        color: '#F15A24',
        borderBottom: '3px solid #F15A24',
    },
    addTabBtn: {
        padding: '10px 15px',
        cursor: 'pointer',
        fontWeight: '800',
        fontSize: '16px',
        color: '#0097b2',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'nowrap',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: 'rgba(0, 151, 178, 0.05)',
            borderRadius: '8px 8px 0 0',
        }
    },
    // Combined Class + Students dialog styles
    classInfoSection: {
        marginBottom: '16px',
        padding: '16px 18px',
        backgroundColor: 'var(--brand-bg, #fafafa)',
        borderRadius: '12px',
        border: '1px solid var(--brand-border, #eee)',
    },
    classInfoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr',
        },
    },
    classInfoField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'var(--brand-surface, #fff)',
            '& fieldset': { borderColor: '#ddd' },
            '&:hover fieldset': { borderColor: '#F15A24' },
            '&.Mui-focused fieldset': { borderColor: '#F15A24' },
        },
        '& .MuiInputBase-input': {
            padding: '9px 12px',
            fontSize: '13px',
            fontWeight: '500',
            color: 'var(--brand-text, #333)',
        },
        '& .MuiInputLabel-root': {
            fontSize: '13px',
            color: 'var(--brand-text-muted, #888)',
            transform: 'translate(12px, 10px) scale(1)',
            '&.Mui-focused, &.MuiFormLabel-filled': {
                transform: 'translate(12px, -8px) scale(0.85)',
                color: '#F15A24',
                fontWeight: '600',
            },
        },
    },
    // ── Input mode toggle ──
    inputModeToggle: {
        display: 'flex',
        gap: '10px',
        marginBottom: '16px',
    },
    inputModeOption: {
        display: 'flex',
        alignItems: 'center',
        padding: '9px 20px',
        borderRadius: '50px',
        border: '1.5px solid var(--brand-border, #ddd)',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: '700',
        color: 'var(--brand-text, #374151)',
        backgroundColor: 'var(--brand-surface, #fff)',
        transition: 'all 0.2s ease',
        userSelect: 'none',
        '&:hover': {
            borderColor: '#FF6900',
            color: '#FF6900',
        },
    },
    inputModeActive: {
        border: '1.5px solid #FF6900 !important',
        color: '#fff !important',
        backgroundColor: '#FF6900 !important',
        '&:hover': {
            borderColor: '#FF6900 !important',
            color: '#fff !important',
        },
    },
    // ── CSV upload section ──
    csvSection: {
        display: 'flex',
        gap: '20px',
        marginBottom: '10px',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    csvCard: {
        flex: 1,
        border: '1.5px solid var(--brand-border, #eee)',
        borderRadius: '16px',
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '10px',
        backgroundColor: 'var(--brand-bg, #fafafa)',
    },
    csvCardIcon: {
        fontSize: '40px !important',
        color: 'var(--brand-text-muted, #ccc)',
        marginBottom: '4px',
    },
    csvCardTitle: {
        fontSize: '16px !important',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
    },
    csvCardDesc: {
        fontSize: '13px !important',
        color: 'var(--brand-text-muted, #999)',
        lineHeight: '1.5 !important',
        minHeight: '36px',
    },
    csvTemplateBtn: {
        background: 'transparent !important',
        color: '#0097b2 !important',
        border: '1.5px solid #0097b2 !important',
        borderRadius: '50px !important',
        padding: '8px 20px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13px !important',
        marginTop: '4px !important',
        '&:hover': {
            background: 'rgba(0,151,178,0.06) !important',
        },
    },
    csvUploadBtn: {
        background: '#F15A24 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '8px 20px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '13px !important',
        marginTop: '4px !important',
        boxShadow: '0 3px 10px rgba(241,90,36,0.2)',
        '&:hover': {
            background: '#e04d18 !important',
        },
    },
    // ── Username column ──
    studentsThUsername: {
        textAlign: 'center',
        padding: '15px 14px',
        backgroundColor: 'var(--brand-bg, #f8f9fa)',
        borderBottom: '2px solid var(--brand-border, #eee)',
        fontSize: '14px',
        fontWeight: '800',
        color: 'var(--brand-text, #444)',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        minWidth: '100px',
    },
    studentsTdUsername: {
        textAlign: 'center',
        padding: '10px 14px',
        borderBottom: '1px solid var(--brand-border, #eee)',
        fontSize: '13px',
        fontWeight: '600',
        color: '#F15A24',
        whiteSpace: 'nowrap',
        backgroundColor: 'rgba(241,90,36,0.03)',
    },
    // View class dialog 
    viewClassDialogPaper: {
        borderRadius: '24px !important',
        overflow: 'visible',
        boxShadow: '0 20px 60px rgba(0,0,0,0.18) !important',
        padding: '8px 8px 4px !important',
        backgroundColor: 'var(--brand-surface, #fff) !important',
    },
    viewClassDialogTitle: {
        color: '#F15A24 !important',
        fontWeight: '800 !important',
        textAlign: 'center !important',
        fontSize: '20px !important',
        paddingRight: '40px !important',
        paddingTop: '16px !important',
    },
    viewClassDialogClose: {
        position: 'absolute !important',
        top: '12px',
        right: '12px',
        zIndex: 1,
        color: 'var(--brand-text-muted, #666) !important',
    },
    viewClassStatsCard: {
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        padding: '16px 18px',
        marginBottom: '16px',
    },
    viewClassStatRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: '8px',
        fontSize: '14px',
        color: 'var(--brand-text, #444)',
        '&:last-child': { marginBottom: 0 },
    },
    viewClassStatLabel: {
        fontWeight: '600 !important',
        color: 'var(--brand-text, #333) !important',
        fontSize: '14px !important',
    },
    viewClassStatValue: {
        fontWeight: '500 !important',
        color: 'var(--brand-text-muted, #555) !important',
        fontSize: '14px !important',
        textAlign: 'right',
    },
    viewClassModulesCard: {
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        padding: '16px 18px',
    },
    viewClassModulesHeading: {
        fontWeight: '800 !important',
        color: '#F15A24 !important',
        fontSize: '15px !important',
        marginBottom: '12px !important',
    },
    viewClassModuleGroupTitle: {
        fontWeight: '700 !important',
        color: '#F15A24 !important',
        fontSize: '13px !important',
        marginBottom: '6px !important',
        marginTop: '10px !important',
    },
    viewClassModuleItem: {
        fontSize: '14px !important',
        color: 'var(--brand-text, #444) !important',
        paddingLeft: '8px !important',
        marginBottom: '4px !important',
        lineHeight: 1.45,
    },
    viewClassModuleType: {
        color: 'var(--brand-text-muted, #888) !important',
        fontSize: '13px !important',
    },
    viewClassEmptyText: {
        fontSize: '14px !important',
        color: 'var(--brand-text-muted, #888) !important',
        textAlign: 'center',
    },
    viewClassCloseBtn: {
        background: 'linear-gradient(90deg, #F15A24 0%, #FF8A00 100%) !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '8px 32px !important',
        fontWeight: '700 !important',
        fontSize: '14px !important',
        textTransform: 'none !important',
        boxShadow: '0 4px 15px rgba(241, 90, 36, 0.3) !important',
        '&:hover': {
            background: 'linear-gradient(90deg, #e04d18 0%, #F15A24 100%) !important',
        },
    },
    desktopOnly: { [theme.breakpoints.down('sm')]: { display: 'none !important' } },
    mobilePrimary: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '8px',
        },
    },
    mobilePrimaryText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '15px',
            fontWeight: 700,
            color: 'var(--brand-text, #222)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileActions: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            gap: '6px',
            alignItems: 'center',
            flexWrap: 'wrap',
            width: '100%',
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: '1px solid var(--brand-border, rgba(255,255,255,0.08))',
        },
    },
    mobileMetaRow: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            paddingTop: '8px',
            borderTop: '1px solid var(--brand-border, #f0f0f0)',
        },
    },
    mobileMetaTag: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11px',
            color: 'var(--brand-text-muted, #555)',
            background: 'var(--brand-bg, #f5f5f5)',
            borderRadius: '20px',
            padding: '2px 8px',
        },
    },
}));

export default useStyles;
